import { LinkProps } from "react-router-dom";
import { IExtRef } from "../types/shared";
import { appNextUrl, getEnvironmentDomain, isLowerCaseMatch } from "@findemdev/findem-helpers";
import { decodeString } from "@findemdev/findem-helpers";
import { IProfile } from "../types/profile";
import { APP_NAMES, AppName } from "../types/app";

export const GITHUB_KEY = 'GitHub';
export const LINKEDIN_KEY = 'LinkedIn';
export const RESEARCH_GATE_KEY = 'researchgate';
export const DOXIMITY_KEY = 'doximity';

const VALID_SOURCE = [
  LINKEDIN_KEY,
  DOXIMITY_KEY,
  RESEARCH_GATE_KEY,
  GITHUB_KEY
] as const;
export const MAX_PROFILES: number = 4;

const isDeadLinkedInExtRef = (profile: IExtRef): boolean =>
  profile.source.toLowerCase() === LINKEDIN_KEY.toLowerCase() &&
  profile.is_dead === true;

// Workaround for antd tooltip + position: fixed
export const getTooltipElement = (triggerNode: HTMLElement) =>
  (triggerNode.parentNode as HTMLElement) || triggerNode;

export const getLinkedInId = (link: string) => {
  let returnVal = "";
  if (link && link.length > 0) {
    const linkComponents = link?.split("/") || [];
    if (linkComponents[linkComponents.length - 1] !== "") {
      returnVal = linkComponents[linkComponents.length - 1];
    } else {
      returnVal = linkComponents[linkComponents.length - 2];
    }
  }
  return returnVal;
};

// Full Page EP link
export const buildStandaloneLink: (params: {
  profileId: string,
  icpId?: string,
  joid?: string,
  tab?: string,
  accountId?: string, // Converted to aId to be less obvious
  anonName?: boolean,
  anonImage?: boolean,
  anonLinks?: boolean,
  channel?: string,
}) => LinkProps['to'] = ({ profileId, icpId, joid, tab, accountId, anonName, anonImage, anonLinks, channel }) => ({
  pathname: `/profiles/${profileId}`,
  search: `${icpId ? `icpid=${icpId}&` : ''}${joid ? `joid=${joid}&` : ''
    }${accountId ? `aId=${accountId}&` : ''
    }${tab ? `tab=${tab}&` : ''}${anonName ? `anonName=true&` : ''}${anonImage ? `anonImage=true&` : ''}${anonLinks ? `anonLinks=true` : ''}${channel ? `channel=${channel}` : ''}`
});


export const getProfiles: (extRefs: IExtRef[]) => IExtRef[] = (profiles) => [
  ...profiles.reduce<IExtRef[]>((acc, profile) => {
    if (
      profile.url &&
      VALID_SOURCE.includes(profile.source as any) &&
      !isDeadLinkedInExtRef(profile) &&
      !acc.find((added) => isLowerCaseMatch(added.url!, profile.url!))
    ) {
      acc.push(profile);
    }
    return acc;
  }, [])
];



export const sanitizeProfiles = (profiles: Record<string, IProfile>): Record<string, IProfile> => {
  const stripHtml = new RegExp(/(<([^>]+)>)/, 'gmi');

  const sanitizedProfiles: Record<string, IProfile> = Object.entries((profiles)).reduce((
    acc: Record<string, IProfile>,
    profileWithId,
  ) => {
    const [prid, profile]: [string, IProfile] = profileWithId;

    acc[prid] = {
      ...profile,
      ...(profile.candidate && {
        candidate: {
          ...profile.candidate,
          full_name: decodeString(profile.candidate?.full_name ?? ''),
        },
      }),
      ...(profile.ext_refs
        ? {
          ext_refs: profile.ext_refs.map((extRefs: IExtRef) => ({
            ...extRefs,
            url: extRefs?.url?.replace(stripHtml, '') ?? null,
          }))
        }
        : {}
      )
    }
    return acc;
  }, {});

  return sanitizedProfiles;
};

// Helper function to safely get import.meta values
export const getImportMetaEnv = (key: string): string | undefined => {
  return import.meta.env[key];
};

export const getAppName = (): AppName => {
  return (getImportMetaEnv('VITE_APP_NAME') as AppName) || APP_NAMES.ESP;
}

export const getPeopleFinderUrl = () => {
  const appName = getAppName();
  const domain = getEnvironmentDomain();
  const currentSubdomain = window.location.hostname.split('.')[0] ?? '';
  if (appName === APP_NAMES.ESP && currentSubdomain.includes('staging')) {
    return 'https://app-next-main.findem.ai';
  }
  if (appName === APP_NAMES.ESP) {
    return `https://exec-app.${domain}`;
  } else {
    const isStaging = window.location.hostname.includes('dev');
    return isStaging ? `https://rra-staging.${domain}` : `https://rra-app.${domain}`;
  }
}

export const getResolvedCrmUrl = () => {
  const appName = getAppName();

  if (appName === APP_NAMES.ESP) {
    const peeopleFindemUrl = getPeopleFinderUrl();

    return peeopleFindemUrl + '/crm';
  } else {
    // always point to enterprise CRM for RRA
    return appNextUrl + '/crm';
  }
}

export const getResolvedPeopleFinderUrl = () => {
  const appName = getAppName();

  if (appName === APP_NAMES.RRA) {
    return getPeopleFinderUrl() + '/searches';
  } else {
    return getPeopleFinderUrl() + '/projects';
  }
}

export const getIntegrationsUrl = () => {
  const appName = getAppName();
  if (appName === APP_NAMES.RRA) {
    return null;
  }
  return getPeopleFinderUrl() + '/integrations';
}
